import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { Contract } from 'src/app/core/models/contract';
import { EventTypes } from 'src/app/core/models/events.model';
import { QuestionnaireAnswer } from 'src/app/core/models/questionnaire.model';
import { ClaimsService } from 'src/app/core/services/claims.service';
import { EventService } from 'src/app/core/services/event.service';
import {
  applyTemplate,
  saveData,
} from 'src/app/core/services/fileTemplater.service';
import { StorageService } from 'src/app/core/services/storage.service';
import { QuestionnaireService } from 'src/app/core/services/survey.service';
import { UserService } from 'src/app/core/services/user.service';
import { PdfModalComponent, PdfModalData, PdfNavItem } from 'src/app/shared/components/simple/communication-attachment/components/pdf-modal/pdf-modal.component';
import { runConditions } from 'src/app/shared/utils/hideif';

@Component({
  selector: 'app-doc-bundle',
  templateUrl: './doc-bundle.component.html',
  styleUrls: ['./doc-bundle.component.scss'],
})
export class DocBundleComponent implements OnInit {
  @Input() contract?: Contract;
  @Input() claimId?: string;
  @Input() hideStatus: boolean = false;
  @Input() bypassRules?: boolean = false;
  @Input() signedContractPath?: string;

  @Output() valid = new EventEmitter<boolean>();

  state: { [id: string]: boolean } = {};
  filledFormUrls: { [docName: string]: { url: string; bucket: string } } = {};
  preloadedUrls: { [docName: string]: string } = {};

  bundle: Contract['configuration']['bundle'] = [];

  isLoading = true;

  signedDocs: any[] = [];
  caseDocs: any[] = [];

  constructor(
    private es: EventService,
    private ss: StorageService,
    private us: UserService,
    private translate: TranslateService,
    private snackbar: MatSnackBar,
    private questionnaireService: QuestionnaireService,
    private claimsService: ClaimsService,
    private dialog: MatDialog
  ) {}

  async ngOnInit() {
    if (this.claimId) {
      try {
        const claim = await this.claimsService.getClaimWithCase(this.claimId);
        const answers = (
          await this.questionnaireService.getSurveysWithAnswersByClaim(claim)
        ).reduce(
          (previous, current) => {
            if (previous[current.slug]) {
              return previous;
            }
            return {
              ...previous,
              [current.slug]: current.answers?.[0],
            };
          },
          {} as {
            [slug: string]: QuestionnaireAnswer;
          }
        );
        if (this.contract?.configuration?.bundle != null) {
          this.bundle = this.contract?.configuration?.bundle.filter(
            (bundle) => {
              if (bundle.hide_if?.logic) {
                // Contexto unificado: condições podem referenciar respostas de
                // questionários (answers.*) ou atributos do claim (claim.attributes.*)
                const context = {
                  answers: (bundle.hide_if.slug ? answers[bundle.hide_if.slug]?.answers : null) ?? {},
                  claim: { attributes: claim.attributes },
                };
                return !runConditions(bundle.hide_if.logic, context);
              } else {
                return true;
              }
            }
          );

          // Check if pre-filled PDF forms exist in storage (filled by EventBridge on questionnaire submit)
          const pdfFormDocs = this.bundle.filter((doc) => doc.pdf_form);
          if (pdfFormDocs.length > 0 && this.contract?.id) {
            await Promise.all(
              pdfFormDocs.map(async (doc) => {
                try {
                  const filledPath = `${this.claimId}/filled-forms/${this.contract!.id}/${doc.path}`;
                  const { data } = await this.ss.supabaseClient.storage
                    .from('claim-documents')
                    .createSignedUrl(filledPath, 60);

                  if (data?.signedUrl) {
                    this.filledFormUrls[doc.name] = {
                      url: filledPath,
                      bucket: 'claim-documents',
                    };
                  }
                } catch (err) {
                  console.error('Filled PDF form not found in storage:', doc.name, err);
                }
              })
            );
          }

          this.signedDocs = [];
          this.caseDocs = [];

          this.bundle.forEach((doc) => {
            if (doc.draw && this.signedContractPath) {
              this.signedDocs.push(doc);
            } else {
              this.caseDocs.push(doc);
            }
          });
        }
      } catch (error) {
        console.error("Couldn't load all data:", error);
        this.bundle = this.contract?.configuration?.bundle ?? [];
      } finally {
        this.isLoading = false;
      }
    } else {
      this.isLoading = false;
    }
    this.valid.emit(this.checkvalid());
    setTimeout(() => this.preloadDocumentsInBackground(), 0);
  }

  private preloadDocumentsInBackground(): void {
    const confidentialDocs = this.bundle.filter(d => d.confidential);
    confidentialDocs.forEach(async (d) => {
      try {
        let bucket = 'contracts';
        let path = `${this.contract?.id}/${d.path}`;

        if (d.pdf_form && this.filledFormUrls[d.name]) {
          bucket = 'claim-documents';
          path = this.filledFormUrls[d.name].url;
        } else if (d.draw && this.signedContractPath) {
          bucket = 'claim-documents';
          path = this.getSignedDocPath(d.path);
        }

        const { data, error } = await this.ss.supabaseClient.storage
          .from(bucket)
          .createSignedUrl(path, 3600);

        if (!error && data?.signedUrl) {
          this.preloadedUrls[d.name] = data.signedUrl;
        }
      } catch {
        // Silent fallback — on-demand loading will be used when the user opens the doc
      }
    });
  }

  view(doc: {
    name: string;
    path: string;
    download_required: boolean;
    confidential?: boolean;
    templated?: boolean;
    read?: any;
    draw?: any;
    pdf_form?: any;
  }) {
    const viewableDocs = this.bundle.filter(d => d.confidential);
    const currentIndex = Math.max(0, viewableDocs.findIndex(d => d.name === doc.name));

    const navigationBundle: PdfNavItem[] = viewableDocs.map(d => {
      let navBucket = 'contracts';
      let navPath = d.path;
      let navContractId: string | undefined = this.contract?.id;

      if (d.pdf_form && this.filledFormUrls[d.name]) {
        navBucket = 'claim-documents';
        navPath = this.filledFormUrls[d.name].url;
        navContractId = undefined;
      } else if (d.draw && this.signedContractPath) {
        navBucket = 'claim-documents';
        navPath = this.getSignedDocPath(d.path);
        navContractId = undefined;
      }

      const navReadData = this.bypassRules
        ? { ...d.read, required: false, scroll_to_end: false, nominimun_seconds_open: 0 }
        : d.read;

      return {
        name: d.name,
        path: navPath,
        bucket: navBucket,
        contractId: navContractId,
        confidential: this.bypassRules ? false : true,
        readData: navReadData,
        preloadedUrl: this.preloadedUrls[d.name],
        onViewed: () => {
          this.state[d.name] = true;
          this.es.pushNew(EventTypes.DOCUMENT_DOWNLOAD, { ...d, action: 'view' });
          this.valid.emit(this.checkvalid());
        },
      } as PdfNavItem;
    });

    const current = navigationBundle[currentIndex] ?? navigationBundle[0];

    const modalData: PdfModalData = {
      path: current.path,
      bucket: current.bucket,
      confidential: current.confidential,
      contractId: current.contractId,
      readData: current.readData,
      navigationBundle,
      currentDocumentIndex: currentIndex,
    };

    const dialogRef = this.dialog.open(PdfModalComponent, {
      width: '90vw',
      maxWidth: '890px',
      height: '90vh',
      maxHeight: '90vh',
      panelClass: 'pdf-modal-panel',
      data: modalData,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((viewed: boolean) => {
      if (!viewed && !this.state[doc.name]) {
        const translatedMessage = this.translate.instant('YouMustReadDocumentToEnd');
        const translatedButton = this.translate.instant('Close');
        this.snackbar.open(translatedMessage, translatedButton, { duration: 5000 });
      }
    });
  }

  async download(doc: {
    name: string;
    path: string;
    download_required: boolean;
    confidential?: boolean;
    templated?: boolean;
    draw?: any;
    pdf_form?: any;
  }) {
    // Não permitir download de documentos confidenciais
    if (doc.confidential) {
      this._showErrorSnackbar('This document is confidential and cannot be downloaded');
      return;
    }
    try {
      if (doc.pdf_form && this.filledFormUrls[doc.name]) {
        const bucket = 'claim-documents';
        const path = this.filledFormUrls[doc.name].url;
        const filename = doc.path;

        const { data: file, error } = await this.ss.download(bucket, path);

        if (error) {
          console.error('Your document could not be downloaded', error);
          this._showErrorSnackbar('Your document could not be downloaded');
          return;
        }

        if (!file) {
          console.error('File not found');
          this._showErrorSnackbar('File not found');
          return;
        }

        saveData(file, filename || doc.name + '.pdf');
      } else if (doc.draw && this.signedContractPath) {
        const bucket = 'claim-documents';
        const filename = `${doc.name}.pdf`;
        const path = this.getSignedDocPath(filename);

        const { data: file, error } = await this.ss.download(bucket, path);

        if (error) {
          console.error('Your document could not be downloaded', error);
          this._showErrorSnackbar('Your document could not be downloaded');
          return;
        }

        if (!file) {
          console.error('File not found');
          this._showErrorSnackbar('File not found');
          return;
        }

        saveData(file, filename || doc.name + '.pdf');
      } else if (doc.templated) {
        const { data: file, error } = await this.ss.download(
          'contracts',
          `${this.contract?.id}/${doc.path}`
        );

        if (error) {
          console.error('Your document could not be downloaded', error);
          this._showErrorSnackbar('Your document could not be downloaded');
          return;
        }

        if (!file) {
          console.error('File not found');
          this._showErrorSnackbar('File not found');
          return;
        }

        const pii = await this.us.getWithPII(
          (
            await this.us.getCurrentUser()
          )?.id!
        );

        const data = await this.getData();

        applyTemplate(await file.arrayBuffer(), data, doc.path);
      } else {
        const { data: file, error } = await this.ss.download(
          'contracts',
          `${this.contract?.id}/${doc.path}`
        );

        if (error) {
          console.error('Your document could not be downloaded', error);
          this._showErrorSnackbar('Your document could not be downloaded');
          return;
        }

        if (!file) {
          console.error('File not found');
          this._showErrorSnackbar('File not found');
          return;
        }

        saveData(file, doc.path);
      }

      this.state[doc.name] = true;
      this.es.pushNew(EventTypes.DOCUMENT_DOWNLOAD, doc);
    } catch (error) {
      console.error(error);
      this._showErrorSnackbar('Your document could not be downloaded');
    }
    this.valid.emit(this.checkvalid());
  }

  private _showErrorSnackbar(message: string) {
    const translatedMessage = this.translate.instant(message);
    const translatedButton = this.translate.instant('Close');
    this.snackbar.open(translatedMessage, translatedButton, {
      duration: 5000,
    });
  }

  checkvalid(): boolean {
    const docs = this.bundle || [];

    let isValidToProceed: boolean = false;

    const docsRequired = docs.filter((item)=>{
      return item.read && item.read.required;
    });

    if(docsRequired.length <= 0){
      isValidToProceed = true;
      return isValidToProceed;
    }

    const allDocsCompleted = docsRequired.every((doc)=>{
      return this.state[doc.name];
    });

    isValidToProceed = allDocsCompleted;

    return isValidToProceed;
  }

  async getData(): Promise<ContractData> {
    const cu = await this.us.getCurrentUser();
    if (!cu) {
      throw new Error('User is not logged in.');
    }

    const u = await this.us.getWithPII(cu.id!);

    if (!u.pii) {
      throw new Error('User does not have PII');
    }

    if (!u.pii.attributes) {
      throw new Error('User does not have PII Attributes');
    }

    const fa = this.formatAddress(u.pii.primary_address);

    // Usar surname ao invés de middle_name + last_name (novo formato)
    const firstName = u.pii.attributes.first_name || '';
    const surname = u.pii.attributes.surname ||
                    // Fallback para formato antigo (middle_name + last_name)
                    `${u.pii.attributes.middle_name || ''} ${u.pii.attributes.last_name || ''}`.trim();

    const name = `${firstName} ${surname}`.replace(/\s+/g, ' ').trim();

    const data: ContractData = {
      title: u.pii.attributes.title || '', // TODO: Add title
      signature: '', // TODO: Add signature
      name,
      firstName: firstName,
      lastName: surname,
      addressLine1: fa.addressLine1,
      addressLine2: fa.addressLine2,
      city: fa.city,
      country: fa.country,
      postcode: fa.postalCode,
      'agreement.dateAgreed': new Date().toLocaleDateString(),
      'signed.date': new Date().toLocaleDateString(),
    };

    return data;
  }

  formatAddress({
    streetNumber = '',
    streetName = '',
    buildingName = '',
    unitNumber = '',
    neighborhood = '',
    city = '',
    state = '',
    county = '',
    country = '',
    postalCode = '',
    postcode = '',
  }: AddressInput): FormattedAddress {
    // Concatenate streetNumber and streetName for addressLine1
    const addressLine1 = [streetNumber, streetName].filter(Boolean).join(' ');

    // Concatenate buildingName, unitNumber, and neighborhood for addressLine2
    const addressLine2 = [buildingName, unitNumber, neighborhood]
      .filter(Boolean)
      .join(', ');

    return {
      addressLine1,
      addressLine2,
      city,
      state,
      county,
      country,
      postalCode: postalCode || postcode || '',
    };
  }

  private getSignedDocPath(filename: string): string {
    let cleanPath = this.signedContractPath || '';
    if (cleanPath.startsWith('claim-documents/')) {
      cleanPath = cleanPath.replace('claim-documents/', '');
    }
    // Remove trailing slash if present to avoid double slashes
    if (cleanPath.endsWith('/')) {
      cleanPath = cleanPath.slice(0, -1);
    }
    return `${cleanPath}/${filename}`;
  }
}

interface ContractData {
  title: string;
  signature: string;
  name: any;
  firstName: any;
  lastName: any;
  addressLine1: any;
  addressLine2: any;
  city: any;
  country: any;
  postcode: any;
  'agreement.dateAgreed': string;
  'signed.date': string;
}

interface FormattedAddress {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  county: string;
  country: string;
  postalCode: string;
}

interface AddressInput {
  streetNumber?: string;
  streetName?: string;
  buildingName?: string;
  unitNumber?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  county?: string;
  country?: string;
  postalCode?: string;
  postcode?: string;
}
